<template>
  <section class="q-pricing">
    <div class="container q-wrap">
      <!-- Header -->
      <div class="text-center q-head">
        <span class="q-pill">
          <i class="bi bi-cash-stack me-2"></i>Pricing
        </span>

        <h2 class="q-title mt-3">
          Simple pricing.
          <span class="q-grad">Bundle</span> what you need.
        </h2>

        <p class="q-sub mt-3 mx-auto">
          Try Quick Quiz AI free for 1 week. Start with the basics, then add premium bundles when you’re ready.
        </p>
      </div>

      <!-- Free trial banner -->
      <div class="q-trial mt-4">
        <div class="q-trial-left">
          <div class="q-trial-title">
            <i class="bi bi-gift me-2"></i>1 week free trial
          </div>
          <div class="q-trial-sub">
            Access the app for 7 days and test the workflow before paying.
          </div>
        </div>

        <a class="btn q-trial-btn" href="/downloads">
          Start Free Trial <i class="bi bi-arrow-right ms-2"></i>
        </a>
      </div>

      <!-- Pricing cards -->
      <div class="row g-4 mt-4">
        <!-- Basic plan -->
        <div class="col-12 col-lg-6">
          <div class="q-card">
            <div class="q-card-top">
              <div class="q-badge">
                <i class="bi bi-star-fill me-2"></i>Basic
              </div>
              <div class="q-card-title">Basic Features</div>
              <div class="q-card-sub">
                Short notes, videos, and mini quizzes.
              </div>
            </div>

            <div class="q-price mt-4">
              <div class="q-price-big">€9.99</div>
              <div class="q-price-right">
                <div class="q-price-meta">first month</div>
                <div class="q-price-meta2">then <b>€19.99 / month</b></div>
              </div>
            </div>

            <ul class="q-list mt-4">
              <li><i class="bi bi-check2-circle"></i> Short Notes</li>
              <li><i class="bi bi-check2-circle"></i> Videos</li>
              <li><i class="bi bi-check2-circle"></i> Mini Quizzes</li>
              <li class="q-muted"><i class="bi bi-lock"></i> Study Plan (bundle add-on)</li>
              <li class="q-muted"><i class="bi bi-lock"></i> Smart Assistant (bundle add-on)</li>
            </ul>

            <div class="d-flex gap-3 flex-wrap mt-4">
              <a class="btn q-cta w-100" href="/downloads">
                Choose Basic
              </a>
            </div>

            <div class="q-footnote mt-3">
              Pricing strategy: basic access is the starter package. Upgrade via bundle pricing.
            </div>
          </div>
        </div>

        <!-- Bundle add-on -->
        <div class="col-12 col-lg-6">
          <div class="q-card q-card-glow">
            <div class="q-card-top">
              <div class="q-badge q-badge-soft">
                <i class="bi bi-box2-heart me-2"></i>Bundle Add-On
              </div>
              <div class="q-card-title">Premium Bundle</div>
              <div class="q-card-sub">
                Unlock additional advanced features.
              </div>
            </div>

            <div class="q-price mt-4">
              <div class="q-price-big">+ €4.99</div>
              <div class="q-price-right">
                <div class="q-price-meta">per month</div>
                <div class="q-price-meta2">add-on bundle</div>
              </div>
            </div>

            <ul class="q-list mt-4">
              <li><i class="bi bi-check2-circle"></i> Study Plan</li>
              <li><i class="bi bi-check2-circle"></i> Smart Assistant</li>
              <li><i class="bi bi-check2-circle"></i> Extra video tools</li>
              <li class="q-muted"><i class="bi bi-info-circle"></i> Requires Basic plan</li>
            </ul>

            <div class="d-flex gap-3 flex-wrap mt-4">
              <a class="btn q-cta w-100" href="/downloads">
                Add Bundle
              </a>
            </div>

            <div class="q-footnote mt-3">
              Bundle pricing: pay only for what you unlock — Basic + Add-On = full experience.
            </div>
          </div>
        </div>
      </div>

      <!-- Total price example -->
      <div class="q-total mt-5">
        <div class="q-total-title">
          <i class="bi bi-calculator me-2"></i>Example Monthly Total
        </div>

        <div class="row g-3 mt-2">
          <div class="col-12 col-lg-4">
            <div class="q-total-card">
              <div class="q-total-n">€19.99</div>
              <div class="q-total-s">Basic after month 1</div>
            </div>
          </div>
          <div class="col-12 col-lg-4">
            <div class="q-total-card">
              <div class="q-total-n">+ €4.99</div>
              <div class="q-total-s">Bundle add-on</div>
            </div>
          </div>
          <div class="col-12 col-lg-4">
            <div class="q-total-card q-total-card-strong">
              <div class="q-total-n">= €24.98</div>
              <div class="q-total-s">Total per month</div>
            </div>
          </div>
        </div>

        <div class="q-total-note mt-3">
          First month total with add-on: <b>€14.98</b> (€9.99 + €4.99), then <b>€24.98/month</b>.
        </div>
      </div>

      <!-- FAQ -->
      <div class="q-faq mt-5">
        <h3 class="q-faq-title">FAQ</h3>

        <div class="row g-3 mt-3">
          <div class="col-12 col-lg-6">
            <div class="q-faq-card">
              <div class="q-faq-q">What do I get in the free week?</div>
              <div class="q-faq-a">
                You can test the app’s workflow for 7 days to see if it fits your study style.
              </div>
            </div>
          </div>

          <div class="col-12 col-lg-6">
            <div class="q-faq-card">
              <div class="q-faq-q">What is included in Basic?</div>
              <div class="q-faq-a">
                Basic includes short notes, videos, and mini quizzes. It’s the core package.
              </div>
            </div>
          </div>

          <div class="col-12 col-lg-6">
            <div class="q-faq-card">
              <div class="q-faq-q">How does bundle pricing work?</div>
              <div class="q-faq-a">
                You start with Basic, then pay +€4.99/month to unlock extra features like Study Plan and Smart Assistant.
              </div>
            </div>
          </div>

          <div class="col-12 col-lg-6">
            <div class="q-faq-card">
              <div class="q-faq-q">Do I need the add-on?</div>
              <div class="q-faq-a">
                Nope — Basic works alone. The add-on is for power users who want the full package.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom CTA -->
      <div class="q-bottom mt-5 text-center">
        <h3 class="q-bottom-t">Start studying smarter today</h3>
        <p class="q-bottom-s mx-auto">
          Try Quick Quiz AI free for a week, then pick Basic or bundle up for the full experience.
        </p>
        <div class="d-flex justify-content-center gap-3 flex-wrap mt-3">
          <a class="btn q-cta" href="/downloads">Start Free Trial</a>
          <a class="btn q-secondary" href="/features">See Features</a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default { name: "PricingView" };
</script>

<style scoped>
.q-pricing { padding: 2.75rem 0 4.5rem; }
.q-wrap { max-width: 72rem; }

.q-head { max-width: 52rem; margin-left: auto; margin-right: auto; }

.q-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.9rem;
  border-radius: 62.5rem;
  background: rgba(255, 255, 255, 0.06);
  border: 0.0625rem solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.85);
  font-weight: 900;
  font-size: 0.95rem;
}

.q-title {
  font-size: clamp(2rem, 3.2vw, 3rem);
  font-weight: 950;
  letter-spacing: -0.06rem;
  line-height: 1.05;
  color: rgba(255, 255, 255, 0.96);
}

.q-grad {
  background: linear-gradient(90deg, #2d6cff 0%, #7c3aed 55%, #ff3d8d 120%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.q-sub {
  font-size: 1.05rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.72);
  max-width: 90%;
}

/* Trial banner */
.q-trial {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.4rem;
  border-radius: 1.75rem;
  background: linear-gradient(90deg, rgba(45,108,255,0.20), rgba(124,58,237,0.20), rgba(255,61,141,0.18));
  border: 0.0625rem solid rgba(255,255,255,0.10);
}

.q-trial-title { font-weight: 950; color: rgba(255,255,255,0.96); }
.q-trial-sub { font-weight: 750; color: rgba(255,255,255,0.72); }

.q-trial-btn {
  border-radius: 62.5rem;
  padding: 0.8rem 1.2rem;
  font-weight: 950;
  color: rgba(255,255,255,0.95);
  border: 0.0625rem solid rgba(255,255,255,0.18);
  background: rgba(0,0,0,0.22);
}
.q-trial-btn:hover { background: rgba(255,255,255,0.06); }

/* Cards */
.q-card {
  height: 100%;
  border-radius: 1.75rem;
  padding: 1.6rem;
  background: rgba(255,255,255,0.04);
  border: 0.0625rem solid rgba(255,255,255,0.08);
  box-shadow: 0 1.75rem 4rem rgba(0,0,0,0.35);
}

.q-card-glow {
  border-color: rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.045);
}

.q-card-title {
  font-weight: 950;
  color: rgba(255,255,255,0.95);
  font-size: 1.35rem;
  margin-top: 0.75rem;
}

.q-card-sub {
  color: rgba(255,255,255,0.70);
  font-weight: 750;
  line-height: 1.6;
  margin-top: 0.35rem;
}

.q-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  border-radius: 62.5rem;
  font-weight: 950;
  font-size: 0.85rem;
  color: rgba(255,255,255,0.90);
  background: rgba(255,255,255,0.06);
  border: 0.0625rem solid rgba(255,255,255,0.14);
}

.q-badge-soft {
  background: rgba(255,61,141,0.12);
  border-color: rgba(255,61,141,0.22);
}

/* Price */
.q-price {
  display: flex;
  align-items: baseline;
  gap: 1rem;
  padding: 1rem;
  border-radius: 1.25rem;
  background: rgba(0,0,0,0.22);
  border: 0.0625rem solid rgba(255,255,255,0.10);
}

.q-price-big {
  font-weight: 950;
  font-size: 2rem;
  color: rgba(255,255,255,0.96);
}

.q-price-meta {
  font-weight: 900;
  color: rgba(255,255,255,0.80);
}

.q-price-meta2 {
  font-weight: 750;
  color: rgba(255,255,255,0.65);
  margin-top: 0.1rem;
}

/* List */
.q-list {
  list-style: none;
  padding-left: 0;
  margin: 0;
  display: grid;
  gap: 0.75rem;
}

.q-list li {
  display: flex;
  gap: 0.7rem;
  align-items: flex-start;
  font-weight: 850;
  color: rgba(255,255,255,0.86);
}

.q-list i { margin-top: 0.1rem; }
.q-muted { color: rgba(255,255,255,0.55); }
.q-muted i { color: rgba(255,255,255,0.45); }

/* Buttons */
.q-cta {
  border-radius: 62.5rem;
  padding: 0.85rem 1.25rem;
  font-weight: 950;
  color: #fff;
  border: 0;
  background: linear-gradient(90deg, #2d6cff 0%, #7c3aed 55%, #ff3d8d 120%);
  box-shadow: 0 1.125rem 2.25rem rgba(0, 0, 0, 0.35);
}
.q-cta:hover { filter: brightness(1.06); transform: translateY(-0.0625rem); }

.q-secondary {
  border-radius: 62.5rem;
  padding: 0.85rem 1.25rem;
  font-weight: 900;
  color: rgba(255,255,255,0.92);
  border: 0.0625rem solid rgba(255,255,255,0.18);
  background: rgba(255,255,255,0.04);
}
.q-secondary:hover { background: rgba(255,255,255,0.08); }

/* Footnote */
.q-footnote {
  color: rgba(255,255,255,0.60);
  font-weight: 750;
  line-height: 1.55;
}

/* Total example */
.q-total {
  padding: 1.6rem;
  border-radius: 1.75rem;
  background: rgba(255,255,255,0.04);
  border: 0.0625rem solid rgba(255,255,255,0.08);
}
.q-total-title { font-weight: 950; color: rgba(255,255,255,0.95); }

.q-total-card {
  border-radius: 1.25rem;
  padding: 1rem;
  background: rgba(0,0,0,0.22);
  border: 0.0625rem solid rgba(255,255,255,0.10);
  text-align: center;
}
.q-total-card-strong {
  background: linear-gradient(90deg, rgba(45,108,255,0.18), rgba(124,58,237,0.18), rgba(255,61,141,0.15));
  border-color: rgba(255,255,255,0.12);
}
.q-total-n { font-weight: 950; font-size: 1.4rem; color: rgba(255,255,255,0.95); }
.q-total-s { font-weight: 850; color: rgba(255,255,255,0.68); }

.q-total-note {
  color: rgba(255,255,255,0.70);
  font-weight: 800;
  line-height: 1.6;
}

/* FAQ */
.q-faq-title { font-weight: 950; color: rgba(255,255,255,0.95); }
.q-faq-card {
  border-radius: 1.5rem;
  padding: 1.25rem;
  background: rgba(0,0,0,0.22);
  border: 0.0625rem solid rgba(255,255,255,0.10);
}
.q-faq-q { font-weight: 950; color: rgba(255,255,255,0.92); margin-bottom: 0.4rem; }
.q-faq-a { color: rgba(255,255,255,0.70); font-weight: 750; line-height: 1.6; }

/* Bottom CTA */
.q-bottom {
  padding: 2rem 1rem;
  border-radius: 1.75rem;
  background: rgba(255,255,255,0.04);
  border: 0.0625rem solid rgba(255,255,255,0.08);
}
.q-bottom-t { font-weight: 950; color: rgba(255,255,255,0.96); margin-bottom: 0.5rem; }
.q-bottom-s { color: rgba(255,255,255,0.72); line-height: 1.65; max-width: 44rem; margin-bottom: 0; }

@media (max-width: 62rem) {
  .q-sub { max-width: 100%; }
  .q-trial { flex-direction: column; align-items: flex-start; }
  .q-trial-btn { width: 100%; text-align: center; }
}
</style>
